import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "../api/api";

const MONATE = ["Januar","Februar","März","April","Mai","Juni",
                "Juli","August","September","Oktober","November","Dezember"];
const WT     = ["So","Mo","Di","Mi","Do","Fr","Sa"];
const WT_ISO = [7, 1, 2, 3, 4, 5, 6]; // JS getDay() → ISO weekday (1=Mo…7=So)

// Lokales Kalenderdatum als "YYYY-MM-DD" – NICHT toISOString() verwenden,
// das konvertiert nach UTC und verschiebt das Datum in Deutschland um einen Tag.
function toLocalISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getRolle(user) {
  const r = user?.rolle ?? "";
  return {
    kannBearbeiten: r === "FahrerAdmin" || r === "MolkereiAdmin" || !!user?.isAdmin,
    istAdmin:       !!user?.isAdmin,
    typ: user?.isAdmin ? null : (r === "Fahrer" || r === "FahrerAdmin") ? "Fahrer" : "Molkerei",
  };
}

function getOstern(jahr) {
  const a=jahr%19,b=Math.floor(jahr/100),c=jahr%100,d=Math.floor(b/4),e=b%4,
    f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30,
    i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,
    m=Math.floor((a+11*h+22*l)/451),
    month=Math.floor((h+l-7*m+114)/31),
    day=((h+l-7*m+114)%31)+1;
  return new Date(jahr,month-1,day);
}

function getFeiertage(jahr, monat) {
  const fix = [[1,1],[5,1],[10,3],[12,25],[12,26]];
  const os = getOstern(jahr);
  const variabel = [
    new Date(os.getFullYear(),os.getMonth(),os.getDate()-2),
    os,
    new Date(os.getFullYear(),os.getMonth(),os.getDate()+1),
    new Date(os.getFullYear(),os.getMonth(),os.getDate()+39),
    new Date(os.getFullYear(),os.getMonth(),os.getDate()+49),
    new Date(os.getFullYear(),os.getMonth(),os.getDate()+50),
  ];
  const tage = new Set();
  fix.forEach(([m,d]) => { if (m === monat) tage.add(d); });
  variabel.forEach(d => { if (d.getMonth()+1 === monat) tage.add(d.getDate()); });
  return tage;
}

export default function Dienstplan() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const rollen    = getRolle(user);

  const heute = new Date();
  const [typ,   setTyp]   = useState(rollen.istAdmin ? "Molkerei" : rollen.typ ?? "Molkerei");
  const [jahr,  setJahr]  = useState(heute.getFullYear());
  const [monat, setMonat] = useState(heute.getMonth() + 1);

  const [mitarbeiter,  setMitarbeiter]  = useState([]);
  const [plan,         setPlan]         = useState(null);
  const [zellen,       setZellen]       = useState({});
  const [bemerkungen,  setBemerkungen]  = useState({});  // tag → text
  const [loading,      setLoading]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [urlaube,      setUrlaube]      = useState([]);

  const tageImMonat = new Date(jahr, monat, 0).getDate();
  const feiertage   = getFeiertage(jahr, monat);
  const istWE = (t) => { const d = new Date(jahr, monat-1, t).getDay(); return d===0||d===6; };
  const wt    = (t) => WT[new Date(jahr, monat-1, t).getDay()];

  const ladePlan = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.dienstplan.get(typ, jahr, monat);
      setPlan(data);
      setMitarbeiter(data.mitarbeiter ?? []);
      const z = {};
      (data.eintraege ?? []).forEach(e => {
        if (e.status === "X") {
          if (!z[e.tag]) z[e.tag] = [];
          if (!z[e.tag].includes(e.userId)) z[e.tag].push(e.userId);
        }
      });
      setZellen(z);
      try {
        setBemerkungen(data.bemerkung ? JSON.parse(data.bemerkung) : {});
      } catch { setBemerkungen({}); }
    } catch {}
    finally { setLoading(false); }
  }, [typ, jahr, monat]);

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    ladePlan();
    api.betriebsurlaub.getAll().then(setUrlaube).catch(() => {});
  }, [typ, jahr, monat, ladePlan]);

  const istBetriebsurlaub = (tag) => {
    const datum = new Date(jahr, monat - 1, tag);
    return urlaube.some(u => {
      const von = new Date(u.urlaubVon + "T00:00:00");
      const bis = new Date(u.urlaubBis + "T00:00:00");
      return datum >= von && datum <= bis;
    });
  };

  const buildMonatRows = (m, zellenM, mitarbeiterM) => {
    const tage = new Date(jahr, m, 0).getDate();
    const ft   = getFeiertage(jahr, m);
    const isWE = (t) => { const d = new Date(jahr, m-1, t).getDay(); return d===0||d===6; };
    const wtag = (t) => WT[new Date(jahr, m-1, t).getDay()];
    const isBU = (t) => {
      const datum = new Date(jahr, m-1, t);
      return urlaube.some(u => {
        const von = new Date(u.urlaubVon + "T00:00:00");
        const bis = new Date(u.urlaubBis + "T00:00:00");
        return datum >= von && datum <= bis;
      });
    };
    return Array.from({ length: tage }, (_, i) => {
      const tag = i + 1;
      const bu  = isBU(tag);
      const ftt = ft.has(tag);
      const bem = bu ? "Betriebsurlaub" : ftt ? "Feiertag (NDS)" : isWE(tag) ? "Wochenende" : "";
      return [
        MONATE[m-1],
        String(tag).padStart(2, "0"),
        wtag(tag),
        ...mitarbeiterM.map(mi => (zellenM[tag] ?? []).includes(mi.userId) ? "X" : ""),
        bem,
      ];
    });
  };

  const downloadCSV = () => {
    const headers = ["Tag", "WT", ...mitarbeiter.map(m => `${m.vorname} ${m.nachname}`), "Bemerkung"];
    const rows = buildMonatRows(monat, zellen, mitarbeiter).map(r => r.slice(1)); // ohne Monatsspalte
    const csv = [headers, ...rows]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";"))
      .join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Dienstplan_${typ}_${MONATE[monat-1]}_${jahr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [jahrDownloading, setJahrDownloading] = useState(false);

  const downloadJahrCSV = async () => {
    setJahrDownloading(true);
    try {
      // Alle 12 Monate laden
      const alleMonate = await Promise.all(
        Array.from({ length: 12 }, (_, i) => i + 1).map(m =>
          api.dienstplan.get(typ, jahr, m).catch(() => ({ mitarbeiter: [], eintraege: [] }))
        )
      );

      // Mitarbeiter-Union über alle Monate (nach userId dedupliziert)
      const alleMA = [];
      const seenIds = new Set();
      alleMonate.forEach(d => {
        (d.mitarbeiter ?? []).forEach(ma => {
          if (!seenIds.has(ma.userId)) { seenIds.add(ma.userId); alleMA.push(ma); }
        });
      });

      const headers = ["Monat", "Tag", "WT", ...alleMA.map(m => `${m.vorname} ${m.nachname}`), "Bemerkung"];
      const rows = [];
      alleMonate.forEach((data, idx) => {
        const m = idx + 1;
        const zellenM = {};
        (data.eintraege ?? []).forEach(e => {
          if (e.status === "X") {
            if (!zellenM[e.tag]) zellenM[e.tag] = [];
            if (!zellenM[e.tag].includes(e.userId)) zellenM[e.tag].push(e.userId);
          }
        });
        rows.push(...buildMonatRows(m, zellenM, alleMA));
        rows.push([]); // Leerzeile zwischen Monaten
      });

      const csv = [headers, ...rows]
        .map(row => row.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";"))
        .join("\r\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dienstplan_${typ}_${jahr}_Jahresübersicht.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally { setJahrDownloading(false); }
  };

  const speichern = async () => {
    setSaving(true);
    const eintraege = [];
    Object.entries(zellen).forEach(([tag, users]) => {
      users.forEach(userId => eintraege.push({ userId, tag: parseInt(tag), status: "X" }));
    });
    // Bemerkungen als JSON im Bemerkung-Feld des Plans speichern
    const bemerkungJson = Object.keys(bemerkungen).length > 0
      ? JSON.stringify(bemerkungen)
      : null;
    await api.dienstplan.save({ typ, jahr, monat, erstelltVon: user?.userId, eintraege, bemerkung: bemerkungJson });
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const toggleZelle = (userId, tag) => {
    if (!rollen.kannBearbeiten) return;
    setZellen(prev => {
      const current = prev[tag] ?? [];
      const hat = current.includes(userId);
      return {
        ...prev,
        [tag]: hat ? current.filter(id => id !== userId) : [...current, userId],
      };
    });
  };

  if (!user) return null;

  const thStyle = {
    background: "#1b5e20",
    color: "white",
    padding: "0.5rem 0.75rem",
    fontWeight: 700,
    fontSize: "0.82rem",
    textAlign: "center",
    whiteSpace: "nowrap",
    border: "1px solid #145214",
  };

  const tdBase = {
    padding: "0.3rem 0.5rem",
    border: "1px solid #ddd",
    fontSize: "0.85rem",
    textAlign: "center",
  };

  return (
    <div style={{ minHeight:"100vh", background:"#f5f7f5", padding:"1.5rem 1rem" }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:"1rem", marginBottom:"1.5rem", flexWrap:"wrap" }}>
        <button onClick={() => navigate(-1)} style={{
          background:"none", border:"1.5px solid #ccc", borderRadius:"8px",
          padding:"0.4rem 0.9rem", cursor:"pointer", color:"#555"
        }}>
          ← Zurück
        </button>
        <div style={{ flex:1 }}>
          <h2 style={{ margin:0, color:"#1b5e20" }}>📋 Dienstplan</h2>
        </div>
      </div>

      {/* Steuerleiste */}
      <div style={{ display:"flex", gap:"0.75rem", flexWrap:"wrap", marginBottom:"1.25rem", alignItems:"center" }}>
        {rollen.istAdmin && (
          <div style={{ display:"flex", gap:"0.4rem" }}>
            {["Fahrer","Molkerei"].map(t => (
              <button key={t} onClick={() => setTyp(t)} style={{
                padding:"0.4rem 1rem", borderRadius:"8px", border:"none", cursor:"pointer",
                fontWeight:600, fontSize:"0.88rem",
                background: typ===t ? "#1b5e20" : "#e0e0e0",
                color: typ===t ? "white" : "#444",
              }}>{t === "Fahrer" ? "🚚 Fahrer" : "🥛 Molkerei"}</button>
            ))}
          </div>
        )}
        <select value={monat} onChange={e => setMonat(+e.target.value)}
          style={{ padding:"0.4rem 0.6rem", borderRadius:"8px", border:"1px solid #ccc", fontSize:"0.9rem" }}>
          {MONATE.map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        <select value={jahr} onChange={e => setJahr(+e.target.value)}
          style={{ padding:"0.4rem 0.6rem", borderRadius:"8px", border:"1px solid #ccc", fontSize:"0.9rem" }}>
          {[heute.getFullYear()-1, heute.getFullYear(), heute.getFullYear()+1].map(j => (
            <option key={j} value={j}>{j}</option>
          ))}
        </select>
        {typ !== "Fahrer" && (
          <div style={{ marginLeft:"auto", display:"flex", gap:"0.5rem", alignItems:"center" }}>
            {mitarbeiter.length > 0 && (
              <>
                <button onClick={downloadCSV} style={{
                  padding:"0.45rem 1.1rem", borderRadius:"8px", border:"1.5px solid #1b5e20",
                  cursor:"pointer", background:"white", color:"#1b5e20", fontWeight:600, fontSize:"0.9rem",
                }}>📥 CSV Monat</button>
                <button onClick={downloadJahrCSV} disabled={jahrDownloading} style={{
                  padding:"0.45rem 1.1rem", borderRadius:"8px", border:"1.5px solid #1b5e20",
                  cursor:"pointer", background:"white", color:"#1b5e20", fontWeight:600, fontSize:"0.9rem",
                }}>{jahrDownloading ? "⏳..." : "📥 CSV Jahr"}</button>
              </>
            )}
            {rollen.kannBearbeiten && (
              <>
                {saved && <span style={{ color:"#2e7d32", fontWeight:600, fontSize:"0.88rem" }}>✓ Gespeichert!</span>}
                {!plan?.dienstplanId && (
                  <span style={{ color:"#e65100", fontSize:"0.82rem" }}>Noch nicht gespeichert</span>
                )}
                <button onClick={speichern} disabled={saving} style={{
                  padding:"0.45rem 1.25rem", borderRadius:"8px", border:"none", cursor:"pointer",
                  background:"#1b5e20", color:"white", fontWeight:700, fontSize:"0.9rem",
                }}>{saving ? "⏳..." : "💾 Speichern"}</button>
              </>
            )}
          </div>
        )}
      </div>

      {typ === "Fahrer" ? (
        <FahrerTourenPlan
          jahr={jahr} monat={monat}
          kannBearbeiten={rollen.kannBearbeiten}
          eigenerId={rollen.kannBearbeiten ? null : user?.userId}
        />
      ) : loading ? (
        <div style={{ textAlign:"center", padding:"4rem", color:"#aaa" }}>Lädt...</div>
      ) : mitarbeiter.length === 0 ? (
        <div style={{ background:"#fff3e0", border:"1.5px solid #ffcc80", borderRadius:"12px",
          padding:"1.25rem 1.5rem", color:"#e65100" }}>
          <strong>Keine Mitarbeiter mit der Rolle "Molkerei" gefunden.</strong><br/>
          <span style={{ fontSize:"0.88rem" }}>
            Bitte im <strong>Admin → Einstellungen → Benutzerverwaltung</strong> die Rolle
            "Molkerei / MolkereiAdmin" zuweisen.
          </span>
        </div>
      ) : (
        <>
          <div style={{ overflowX:"auto" }}>
            <table style={{ borderCollapse:"collapse", width:"100%", minWidth: 400 + mitarbeiter.length * 110 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width:50 }}>Tag</th>
                  <th style={{ ...thStyle, width:44 }}>WT</th>
                  {mitarbeiter.map(m => (
                    <th key={m.userId} style={{ ...thStyle, minWidth:100 }}>
                      {m.vorname}<br/>
                      <span style={{ fontWeight:400, opacity:0.85 }}>{m.nachname}</span>
                    </th>
                  ))}
                  <th style={{ ...thStyle, minWidth:160 }}>Bemerkung</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: tageImMonat }, (_, i) => i + 1).map(tag => {
                  const we = istWE(tag);
                  const ft = feiertage.has(tag);
                  const bu = istBetriebsurlaub(tag);
                  const rowBg = bu ? "#ede7f6" : ft ? "#fff9c4" : we ? "#fce4ec" : "white";
                  const labelColor = bu ? "#6a1b9a" : ft ? "#f57f17" : we ? "#c62828" : "#333";

                  return (
                    <tr key={tag} style={{ background: rowBg }}>
                      <td style={{ ...tdBase, fontWeight:700, color: labelColor }}>
                        {String(tag).padStart(2,"0")}
                      </td>
                      <td style={{ ...tdBase, color: labelColor, fontWeight: we || ft ? 700 : 400 }}>
                        {wt(tag)}
                      </td>
                      {mitarbeiter.map(m => {
                        const hat = (zellen[tag] ?? []).includes(m.userId);
                        return (
                          <td key={m.userId}
                            onClick={() => toggleZelle(m.userId, tag)}
                            style={{
                              ...tdBase,
                              cursor: rollen.kannBearbeiten ? "pointer" : "default",
                              background: hat ? "#c8e6c9" : rowBg,
                              fontWeight: hat ? 700 : 400,
                              color: hat ? "#1b5e20" : "#bbb",
                              fontSize: "1rem",
                              userSelect: "none",
                            }}>
                            {hat ? "X" : ""}
                          </td>
                        );
                      })}
                      <td style={{ ...tdBase, textAlign:"left", fontSize:"0.78rem", padding:"0.2rem 0.4rem" }}>
                        {bu
                          ? <span style={{ color:"#6a1b9a", fontWeight:600 }}>🏖️ Betriebsurlaub</span>
                          : rollen.kannBearbeiten
                            ? <input
                                value={bemerkungen[tag] ?? ""}
                                onChange={e => {
                                  const v = e.target.value;
                                  setBemerkungen(prev => v ? { ...prev, [tag]: v } : (({ [tag]: _, ...rest }) => rest)(prev));
                                  setSaved(false);
                                }}
                                placeholder="Bemerkung…"
                                style={{ width:"100%", border:"none", background:"transparent", fontSize:"0.78rem", color:"#333", outline:"none", padding:"0.1rem 0" }}
                              />
                            : <span style={{ color:"#888" }}>{bemerkungen[tag] ?? ""}</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Legende */}
          <div style={{ display:"flex", gap:"1.25rem", marginTop:"0.85rem", fontSize:"0.78rem", color:"#888", flexWrap:"wrap" }}>
            <span style={{ display:"flex", alignItems:"center", gap:"0.3rem" }}>
              <span style={{ width:12, height:12, borderRadius:3, background:"#fce4ec", border:"1px solid #e57373", display:"inline-block" }}/>Wochenende
            </span>
            <span style={{ display:"flex", alignItems:"center", gap:"0.3rem" }}>
              <span style={{ width:12, height:12, borderRadius:3, background:"#fff9c4", border:"1px solid #f9a825", display:"inline-block" }}/>Feiertag (NDS)
            </span>
            <span style={{ display:"flex", alignItems:"center", gap:"0.3rem" }}>
              <span style={{ width:12, height:12, borderRadius:3, background:"#ede7f6", border:"1px solid #9c27b0", display:"inline-block" }}/>Betriebsurlaub
            </span>
            <span style={{ display:"flex", alignItems:"center", gap:"0.3rem" }}>
              <span style={{ width:12, height:12, borderRadius:3, background:"#c8e6c9", border:"1px solid #81c784", display:"inline-block" }}/>Dienst eingetragen
            </span>
            {rollen.kannBearbeiten && (
              <span style={{ fontStyle:"italic" }}>💡 Auf eine Zelle klicken um X zu setzen / entfernen</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Hilfsfunktionen Dauer ──────────────────────────────────
function minutenZuHHMM(min) {
  if (min == null || min === "") return "";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function hhmmZuMinuten(str) {
  if (!str) return null;
  str = str.trim();
  if (/^\d+$/.test(str)) return parseInt(str, 10);
  const m = str.match(/^(\d+):(\d{0,2})$/);
  if (m) return parseInt(m[1], 10) * 60 + (parseInt(m[2] || "0", 10) || 0);
  return null;
}

// ── Fahrer-Touren-Plan ─────────────────────────────────────
function FahrerTourenPlan({ jahr, monat, kannBearbeiten, eigenerId }) {
  const [daten,      setDaten]      = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState({});   // "datum-tourid" → bool
  const [dauerLocal, setDauerLocal] = useState({});   // id → Eingabe-String

  useEffect(() => {
    setLoading(true);
    api.fahrerTourenPlan.get(jahr, monat)
      .then(data => {
        setDaten(data);
        // Initialwerte für Dauer-Felder
        const init = {};
        (data.zuweisungen ?? []).forEach(z => {
          init[z.id] = minutenZuHHMM(z.dauerMinuten);
        });
        setDauerLocal(init);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [jahr, monat]);

  const setZuweisung = async (datum, tourId, userId) => {
    const key = `${datum}-${tourId}`;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      await api.fahrerTourenPlan.setzen(datum, tourId, parseInt(userId));
      setDaten(prev => {
        const rest = (prev.zuweisungen ?? []).filter(z => !(z.datum === datum && z.tourId === tourId));
        if (parseInt(userId) === 0) return { ...prev, zuweisungen: rest };
        const fahrer = (prev.fahrer ?? []).find(f => f.userId === parseInt(userId));
        return { ...prev, zuweisungen: [...rest, {
          id: null, datum, tourId, userId: parseInt(userId), dauerMinuten: null,
          vorname: fahrer?.vorname ?? "", nachname: fahrer?.nachname ?? "",
        }]};
      });
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const saveDauer = async (id, inputStr) => {
    if (id == null) return;
    const minuten = hhmmZuMinuten(inputStr);
    try {
      await api.fahrerTourenPlan.setDauer(id, minuten);
      setDaten(prev => ({
        ...prev,
        zuweisungen: (prev.zuweisungen ?? []).map(z =>
          z.id === id ? { ...z, dauerMinuten: minuten } : z
        ),
      }));
    } catch {}
  };

  if (loading) return <div style={{ textAlign:"center", padding:"4rem", color:"#aaa" }}>Lädt...</div>;
  if (!daten)  return null;

  const { touren, fahrer, zuweisungen } = daten;
  const tageImMonat = new Date(jahr, monat, 0).getDate();

  // Lookup: "datum-tourid" → { userId, id, dauerMinuten }
  const lookup = {};
  zuweisungen.forEach(z => { lookup[`${z.datum}-${z.tourId}`] = z; });

  const tageDatum = (tag) => {
    const d = new Date(jahr, monat - 1, tag);
    return toLocalISO(d);
  };

  const tagWochentag = (tag) => {
    const d = new Date(jahr, monat - 1, tag);
    return WT_ISO[d.getDay()];
  };

  const tourenFuerTag = (tag) => {
    const iso = tagWochentag(tag);
    return touren.filter(t =>
      t.wochentage.split(",").map(s => parseInt(s.trim())).includes(iso)
    );
  };

  const thStyle = {
    background: "#1b5e20", color: "white",
    padding: "0.4rem 0.6rem", fontWeight: 700, fontSize: "0.8rem",
    textAlign: "left", border: "1px solid #145214", whiteSpace: "nowrap",
  };
  const tdBase = { padding: "0.3rem 0.5rem", border: "1px solid #e0e0e0", fontSize: "0.85rem", verticalAlign: "middle" };

  // Fahrer-Ansicht: nur eigene Touren
  if (!kannBearbeiten) {
    const eigeneZuweisungen = zuweisungen
      .filter(z => z.userId === eigenerId)
      .sort((a, b) => a.datum.localeCompare(b.datum));

    // Monatsübersicht
    const anzahlTouren = eigeneZuweisungen.length;
    const gesamtMinuten = eigeneZuweisungen.reduce((s, z) => s + (z.dauerMinuten ?? 0), 0);

    if (eigeneZuweisungen.length === 0)
      return (
        <div style={{ background:"#f5f5f5", borderRadius:"10px", padding:"2rem", textAlign:"center", color:"#aaa" }}>
          Keine Touren für dich in {MONATE[monat-1]} {jahr} eingeplant.
        </div>
      );
    return (
      <>
        {/* Monatsübersicht */}
        <div style={{ display:"flex", gap:"1rem", marginBottom:"1rem", flexWrap:"wrap" }}>
          <div style={{ background:"#e8f5e9", border:"1.5px solid #a5d6a7", borderRadius:"10px",
            padding:"0.75rem 1.25rem", minWidth:140 }}>
            <div style={{ fontSize:"0.75rem", color:"#555", marginBottom:2 }}>Touren im Monat</div>
            <div style={{ fontSize:"1.6rem", fontWeight:700, color:"#1b5e20" }}>{anzahlTouren}</div>
          </div>
          <div style={{ background:"#e3f2fd", border:"1.5px solid #90caf9", borderRadius:"10px",
            padding:"0.75rem 1.25rem", minWidth:160 }}>
            <div style={{ fontSize:"0.75rem", color:"#555", marginBottom:2 }}>Gesamtstunden</div>
            <div style={{ fontSize:"1.6rem", fontWeight:700, color:"#1565c0" }}>
              {minutenZuHHMM(gesamtMinuten) || "–"}
            </div>
          </div>
        </div>

        <div style={{ overflowX:"auto" }}>
          <table style={{ borderCollapse:"collapse", width:"100%" }}>
            <thead>
              <tr>
                <th style={thStyle}>Datum</th>
                <th style={thStyle}>WT</th>
                <th style={thStyle}>Tour</th>
                <th style={thStyle}>Dauer</th>
              </tr>
            </thead>
            <tbody>
              {eigeneZuweisungen.map(z => {
                const tour = touren.find(t => t.tourId === z.tourId);
                const d = new Date(z.datum + "T00:00:00");
                const wtLabel = WT[d.getDay()];
                const tag = d.getDate();
                const isWE = d.getDay() === 0 || d.getDay() === 6;
                const entryId = z.id;
                return (
                  <tr key={`${z.datum}-${z.tourId}`} style={{ background: isWE ? "#fce4ec" : "white" }}>
                    <td style={{ ...tdBase, fontWeight: 700 }}>{String(tag).padStart(2,"0")}.{String(monat).padStart(2,"0")}.</td>
                    <td style={{ ...tdBase, color: isWE ? "#c62828" : "#333", fontWeight: isWE ? 700 : 400 }}>{wtLabel}</td>
                    <td style={{ ...tdBase }}>
                      <span style={{ display:"inline-block", width:10, height:10, borderRadius:"50%", background: tour?.farbe ?? "#888", marginRight:6 }}/>
                      {tour?.name ?? `Tour ${z.tourId}`}
                    </td>
                    <td style={{ ...tdBase, padding:"0.2rem 0.4rem" }}>
                      <input
                        type="text"
                        placeholder="HH:MM"
                        value={entryId != null ? (dauerLocal[entryId] ?? "") : ""}
                        onChange={e => entryId != null && setDauerLocal(prev => ({ ...prev, [entryId]: e.target.value }))}
                        onBlur={e => saveDauer(entryId, e.target.value)}
                        style={{
                          width: 70, padding: "0.2rem 0.4rem", borderRadius: 6,
                          border: "1px solid #ccc", fontSize: "0.85rem",
                          background: z.dauerMinuten ? "#e3f2fd" : "white",
                          textAlign: "center",
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize:"0.75rem", color:"#aaa", marginTop:"0.5rem" }}>
          Dauer als HH:MM (z.B. 2:30) oder in Minuten (z.B. 150) eingeben · wird automatisch gespeichert
        </div>
      </>
    );
  }

  // Admin/FahrerAdmin-Ansicht: volle Tabelle mit Dropdowns
  const rows = [];
  for (let tag = 1; tag <= tageImMonat; tag++) {
    const tagTouren = tourenFuerTag(tag);
    if (tagTouren.length === 0) continue;
    const datum = tageDatum(tag);
    const d     = new Date(jahr, monat - 1, tag);
    const wtLabel = WT[d.getDay()];
    const isWE  = d.getDay() === 0 || d.getDay() === 6;
    rows.push({ tag, datum, tagTouren, wtLabel, isWE });
  }

  if (rows.length === 0)
    return (
      <div style={{ background:"#fff3e0", border:"1.5px solid #ffcc80", borderRadius:"12px", padding:"1.25rem 1.5rem", color:"#e65100" }}>
        Keine aktiven Touren gefunden. Bitte Touren im Admin-Bereich anlegen.
      </div>
    );

  return (
    <div style={{ overflowX:"auto" }}>
      <table style={{ borderCollapse:"collapse", width:"100%", minWidth:500 }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width:50 }}>Tag</th>
            <th style={{ ...thStyle, width:44 }}>WT</th>
            <th style={{ ...thStyle }}>Tour</th>
            <th style={{ ...thStyle, minWidth:200 }}>Fahrer</th>
            <th style={{ ...thStyle, width:90 }}>Dauer</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ tag, datum, tagTouren, wtLabel, isWE }) =>
            tagTouren.map((tour, ti) => {
              const key     = `${datum}-${tour.tourId}`;
              const z       = lookup[key];
              const userId  = z?.userId ?? 0;
              const entryId = z?.id ?? null;
              const isSaving = saving[key];
              return (
                <tr key={key} style={{ background: ti === 0 && isWE ? "#fce4ec" : ti === 0 ? "white" : "#fafafa" }}>
                  {ti === 0 && (
                    <>
                      <td style={{ ...tdBase, fontWeight:700, color: isWE ? "#c62828" : "#333" }}
                          rowSpan={tagTouren.length}>
                        {String(tag).padStart(2,"0")}
                      </td>
                      <td style={{ ...tdBase, color: isWE ? "#c62828" : "#333", fontWeight: isWE ? 700 : 400 }}
                          rowSpan={tagTouren.length}>
                        {wtLabel}
                      </td>
                    </>
                  )}
                  <td style={{ ...tdBase }}>
                    <span style={{ display:"inline-block", width:10, height:10, borderRadius:"50%", background: tour.farbe, marginRight:6 }}/>
                    {tour.name}
                  </td>
                  <td style={{ ...tdBase, padding:"0.2rem 0.4rem" }}>
                    <select
                      value={userId}
                      disabled={isSaving}
                      onChange={e => setZuweisung(datum, tour.tourId, e.target.value)}
                      style={{ width:"100%", padding:"0.25rem 0.4rem", borderRadius:6, border:"1px solid #ccc", fontSize:"0.85rem", background: userId ? "#e8f5e9" : "white" }}>
                      <option value={0}>– kein Fahrer –</option>
                      {fahrer.map(f => (
                        <option key={f.userId} value={f.userId}>{f.vorname} {f.nachname}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...tdBase, padding:"0.2rem 0.4rem", textAlign:"center" }}>
                    {entryId != null ? (
                      <span style={{ fontSize:"0.82rem", color: z?.dauerMinuten ? "#1565c0" : "#bbb" }}>
                        {minutenZuHHMM(z?.dauerMinuten) || "–"}
                      </span>
                    ) : <span style={{ color:"#ddd" }}>–</span>}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

